import { ModuleType } from 'infrastructure/enums';
import {
  SMS_ACTIVITIES_PER_PAGE,
  useGetClinicalNotes,
} from 'infrastructure/hooks/ccm';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { useState } from 'react';
import {
  isApcmActionAllowedStatus,
  isCcmActionAllowedStatus,
} from 'utils/userTypeHelper';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { useCheckPatientModule } from 'infrastructure/hooks/patient/use-check-patient-module';
import { useDeleteClinicalNotes } from 'infrastructure/hooks/ccm/medical-activity/use-delete-clinical-notes';
import useUser from 'utils/useUser';

import type { DateRange, SortState } from 'infrastructure/types';
import type { CcmStatuses } from 'infrastructure/enums';

interface IProps {
  patientGuid: string;
  dateRange: DateRange | [];
  module: ModuleType;
  documentationTimeSort?: SortState;
  createdAtSort?: SortState;
}

export const useClinicNotes = ({
  patientGuid,
  module,
  dateRange,
  documentationTimeSort,
  createdAtSort,
}: IProps) => {
  const [page, setPage] = useState(1);
  const [opened, setOpened] = useState(false);

  const { clinicalNotes, isLoading: isClinicalNotesLoading } =
    useGetClinicalNotes({
      patientGuid,
      dateRange,
      module,
      documentationTimeSort,
      createdAtSort,
      meta: { items: SMS_ACTIVITIES_PER_PAGE, page },
    });

  const { patientInfo, loading: isPatientInfoLoading } = usePatientInfo({
    patientGuid,
  });

  const { deleteClinicalNotesAsync, deleteLoading } = useDeleteClinicalNotes({
    module,
    page,
    patientGuid,
  });

  const { userType } = useUser();

  const { isReadOnlyRole } = useCheckRoleAccess();
  const { isBlockCcmModule } = useCheckPatientModule(patientGuid);

  const ccmDisableButton =
    (isBlockCcmModule ||
      !isCcmActionAllowedStatus(patientInfo?.ccmStatus as CcmStatuses)) &&
    module === ModuleType.CCM;
  const apcmDisableButton =
    !isApcmActionAllowedStatus(patientInfo?.apcmStatus) &&
    module === ModuleType.APCM;

  const disableButton = isReadOnlyRole || ccmDisableButton || apcmDisableButton;

  const deleteNote = async (guid: string) => {
    await deleteClinicalNotesAsync({ patientGuid, guid, module, userType });
  };

  const toggleModal = () => setOpened((prev) => !prev);

  return {
    clinicalNotes,
    loading: isClinicalNotesLoading || isPatientInfoLoading,
    deleteLoading,
    disableButton,
    page,
    setPage,
    opened,
    toggleOpen: toggleModal,
    deleteNote,
  };
};
